import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';

function PrintOnly({ image, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const [printType, setPrintType] = useState('canvas');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const canvasRef = useRef(null);
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);

  const printOptions = [
    { id: 'canvas', label: 'Canvas' },
    { id: 'archival', label: 'Archival Mat Finish 310gsm' },
    { id: 'art-decor', label: 'Art Decor Matt 235gsm' }
  ];

  /* ===== PRICE CONFIG ===== */
  const priceMap = {
    canvas: 0.1,
    'archival': 0.1,
    'art-decor': 0.1
  };
  const GST_RATE = 0.1; // 10%

  /* ===== GST + Total ===== */
  const price = priceMap[printType];
  const gstAmount = price * GST_RATE;
  const totalWithGST = price + gstAmount;
  const getFilterStyle = () => {
    switch (printType) {
      case 'canvas': return { filter: 'contrast(1.05) saturate(1.1)' };
      case 'archival': return { filter: 'contrast(1.05) saturate(0.95)' };
      case 'art-decor': return { filter: 'contrast(0.95) saturate(0.9) brightness(0.98)' };
      default: return {};
    }
  };

  /* ===== IMAGE TO BASE64 ===== */
  const convertImageToBase64 = async (url) => {
    if (!url) return null;

    const res = await fetch(url);
    const blob = await res.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {
    if (!userImage) {
      alert("Please upload an image first.");
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      // ✅ ALWAYS upload cropped user image (NOT canvas)
      const resImg = await fetch(userImage, { cache: 'no-store' });
      const blob = await resImg.blob();

      const uploadedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });


      const payload = {
        id: 8430,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },
          { key: 'print_price', value: totalWithGST.toFixed(2) },
          { key: 'edited_image', value: uploadedImageBase64 },
        ]
      };
      console.log('Payload for API:', payload);
      const response = await axios.post(
        `https://opticonframing.com.au/wp-json/opticon/v1/print-only/add-to-cart`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);   // ✅ real dynamic %
          },
        }
      );

      if (response.data.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);
      } else {
        throw new Error(response.data.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const filterStyle = getFilterStyle();
      ctx.filter = filterStyle.filter || 'none';

      const canvasRatio = canvas.width / canvas.height;
      const imgRatio = img.width / img.height;
      let drawWidth, drawHeight, offsetX, offsetY;

      if (imgRatio > canvasRatio) {
        drawWidth = canvas.width;
        drawHeight = canvas.width / imgRatio;
        offsetX = 0;
        offsetY = (canvas.height - drawHeight) / 2;
      } else {
        drawHeight = canvas.height;
        drawWidth = canvas.height * imgRatio;
        offsetY = 0;
        offsetX = (canvas.width - drawWidth) / 2;
      }
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      ctx.filter = 'none';
    };
    img.src = userImage;
  }, [userImage, printType]);

  useEffect(() => {
    if (!userImage) {
      window.location.href = '/';   // force upload flow
    }
  }, [userImage]);
  console.log('Stored image:', localStorage.getItem('opticon_last_image'));

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);


  return (
    <div className="product-page">

      {/* ===== UPDATED LOADER WITH REQUESTED COLORS ===== */}
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={680}
          height={460}
          className="real-canvas-preview"
        />
      </div>

      {/* RIGHT : PRODUCT INFO */}
      <div className="product-info-panel">

        <h1 className="product-title">Print Only</h1>

        <div className="print-description">
          {printType === 'canvas' && 'Premium textured canvas print with rich depth and artistic finish.'}
          {printType === 'archival' && 'Museum-grade matte finish for long-term preservation.'}
          {printType === 'art-decor' && 'Soft matte artistic paper with elegant tones.'}
        </div>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="image-size-row">
            <span className="form-label">Image Size</span>
            <span className="image-size-value">
              {imageSizeCm.widthCm}×{imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={onEditImage}
              title="Edit image"
            >
              <i class="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Printing option row */}
        <div className="print-option-row">

          <label className="form-label">Printing Options</label>

          <select
            className="form-select"
            value={printType}
            onChange={(e) => setPrintType(e.target.value)}
          >
            {printOptions.map(opt => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price + Add to Cart */}
        <div className="price-cart-row">
          <div className="product-price">
            ${priceMap[printType].toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)}= ${totalWithGST.toFixed(2)}
          </div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>

      </div>
    </div>
  );
}

export default PrintOnly;